import { useState } from "react";

import { downloadReport } from "../api/reports";
import type { CityReportData } from "../types";

function formatInt(n: number | undefined | null): string {
  return typeof n === "number" ? n.toLocaleString("en-US") : "—";
}

function growthLabel(growth: number | null | undefined, previousMonth?: string): string | null {
  if (growth === null || growth === undefined) return null;
  const suffix = previousMonth ? ` vs ${previousMonth}` : " vs last month";
  const sign = growth >= 0 ? "+" : "";
  return `${sign}${growth.toFixed(1)}%${suffix}`;
}

function Metric({ label, value, delta }: { label: string; value: string; delta?: string | null }) {
  const deltaClass = delta && delta.trim().startsWith("+") ? "metric-delta up" : "metric-delta down";
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {delta && <div className={deltaClass}>{delta}</div>}
    </div>
  );
}

export function DataCard({ data }: { data: CityReportData }) {
  const [stationsOpen, setStationsOpen] = useState(true);
  const [rtosOpen, setRtosOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!data) return null;

  const local = data.local ?? null;
  const radiusRegs = data.radius_registrations ?? [];
  const localStations = data.local_stations ?? [];
  const radiusStations = data.radius_stations ?? [];

  const localTotal = local ? local.total_evs : 0;
  const radiusTotal = radiusRegs.reduce((sum, r) => sum + (r.total_evs || 0), 0);

  const rawTitle = data.city ?? (data.coords ? `(${data.coords[0].toFixed(3)}, ${data.coords[1].toFixed(3)})` : "Location");
  const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);

  const allStations = [...localStations, ...radiusStations];

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadReport(data);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Unable to generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="data-card">
      <h4 className="data-card-title">{title}</h4>

      <div className="metrics-row">
        <Metric
          label="Local Total"
          value={local ? formatInt(localTotal) : "—"}
          delta={growthLabel(data.local_growth_percent, data.previous_month)}
        />
        <Metric
          label={`Within ${(data.radius_km ?? 100).toFixed(0)}km`}
          value={formatInt(radiusTotal)}
          delta={growthLabel(data.radius_growth_percent, data.previous_month)}
        />
      </div>

      {local && (
        <>
          <div className="section-caption">Local breakdown</div>
          <div className="metrics-row metrics-row-3">
            <Metric label="Cars" value={formatInt(local.breakdown?.Cars ?? 0)} />
            <Metric label="Buses" value={formatInt(local.breakdown?.Buses ?? 0)} />
            <Metric label="Trucks" value={formatInt(local.breakdown?.Trucks ?? 0)} />
          </div>
          <div className="muted-caption">
            Combined from {local.matched_rtos?.length ?? 0} RTO(s): {(local.matched_rtos ?? []).join(", ")}
          </div>
        </>
      )}

      {radiusRegs.length > 0 && (
        <details className="expander" open={rtosOpen} onToggle={(e) => setRtosOpen(e.currentTarget.open)}>
          <summary>Nearby RTOs ({radiusRegs.length})</summary>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>RTO</th>
                  <th>Total EVs</th>
                  <th>Distance (km)</th>
                </tr>
              </thead>
              <tbody>
                {radiusRegs.slice(0, 20).map((r, i) => (
                  <tr key={`${r.rto_name}_${i}`}>
                    <td>{r.rto_name}</td>
                    <td>{formatInt(r.total_evs)}</td>
                    <td>{r.distance_km.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {allStations.length > 0 ? (
        <details className="expander" open={stationsOpen} onToggle={(e) => setStationsOpen(e.currentTarget.open)}>
          <summary>Charging stations ({allStations.length})</summary>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Station</th>
                  <th>Highway</th>
                  <th>CUF (%)</th>
                  <th>TUF (%)</th>
                </tr>
              </thead>
              <tbody>
                {allStations.map((s, i) => {
                  const highway = [s.nh, s.sh].filter(Boolean).join(" · ") || "No highway match";
                  const cuf = s.cuf_installed_percent;
                  const tuf = s.tuf_installed_percent;

                  return (
                    <tr key={`${s.station_name ?? "station"}_${i}`}>
                      <td>{s.station_name ?? "Unknown"}</td>
                      <td>{highway}</td>
                      <td>{cuf != null ? `${Number(cuf).toFixed(2)}%` : "—"}</td>
                      <td>{tuf != null ? `${Number(tuf).toFixed(2)}%` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      ) : (
        <div className="alert alert-info">No charging station data available for this location.</div>
      )}

      <div className="report-download">
        <button className="btn btn-secondary" onClick={handleDownload} disabled={downloading}>
          {downloading ? "Preparing PDF…" : "Download PDF Report"}
        </button>
        {downloadError && <div className="alert alert-error">{downloadError}</div>}
      </div>
    </div>
  );
}
